import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const CatalogBrowseFilterContext = createContext(null);

const normalizeTagKey = (tag) => String(tag || "").trim();

/**
 * Shared vibe/tag filters for the public catalog browse list
 * (non-featured only). Welcome + featured players can set filters too.
 */
export function CatalogBrowseFilterProvider({ children }) {
  const [selectedTags, setSelectedTags] = useState([]);

  const isTagSelected = useCallback(
    (tag) => {
      const key = normalizeTagKey(tag).toLowerCase();
      if (!key) return false;
      return selectedTags.some((t) => t.toLowerCase() === key);
    },
    [selectedTags],
  );

  const toggleTag = useCallback((tag) => {
    const next = normalizeTagKey(tag);
    if (!next) return;
    setSelectedTags((prev) => {
      const exists = prev.some(
        (t) => t.toLowerCase() === next.toLowerCase(),
      );
      if (exists) {
        return prev.filter((t) => t.toLowerCase() !== next.toLowerCase());
      }
      return [...prev, next];
    });
  }, []);

  const addTag = useCallback((tag) => {
    const next = normalizeTagKey(tag);
    if (!next) return;
    setSelectedTags((prev) => {
      if (prev.some((t) => t.toLowerCase() === next.toLowerCase())) {
        return prev;
      }
      return [...prev, next];
    });
  }, []);

  const removeTag = useCallback((tag) => {
    const key = normalizeTagKey(tag).toLowerCase();
    if (!key) return;
    setSelectedTags((prev) =>
      prev.filter((t) => t.toLowerCase() !== key),
    );
  }, []);

  const clearTags = useCallback(() => {
    setSelectedTags([]);
  }, []);

  /** Apply tag filter and scroll to the catalog browse strip. */
  const applyTagAndScroll = useCallback(
    (tag) => {
      addTag(tag);
      // Let React paint the filter bar, then scroll
      window.requestAnimationFrame(() => {
        const el =
          document.getElementById("catalog-browse") ||
          document.getElementById("music-catalog");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    [addTag],
  );

  const value = useMemo(
    () => ({
      selectedTags,
      isTagSelected,
      toggleTag,
      addTag,
      removeTag,
      clearTags,
      applyTagAndScroll,
      hasTagFilters: selectedTags.length > 0,
    }),
    [
      selectedTags,
      isTagSelected,
      toggleTag,
      addTag,
      removeTag,
      clearTags,
      applyTagAndScroll,
    ],
  );

  return (
    <CatalogBrowseFilterContext.Provider value={value}>
      {children}
    </CatalogBrowseFilterContext.Provider>
  );
}

const noopContext = {
  selectedTags: [],
  isTagSelected: () => false,
  toggleTag: () => {},
  addTag: () => {},
  removeTag: () => {},
  clearTags: () => {},
  applyTagAndScroll: () => {},
  hasTagFilters: false,
};

export function useCatalogBrowseFilter() {
  const ctx = useContext(CatalogBrowseFilterContext);
  return ctx || noopContext;
}

export default CatalogBrowseFilterContext;
